// SmartSearchProvider.ts - Rewritten to reuse allMovies provider cache
import { playListPassword, playListUsername, playListUrl } from "../../../state";
import { Buffer } from "buffer";
import { dnsResolver } from "./dnsResolver"; // Import your DNS resolver
import allMoviesProvider from "./allMovies";

class SmartSearchProvider {
  private static instance: SmartSearchProvider;
  private currentPlaylistKey: string | null = null;
  private _searchKey = 0;
  private moviesLoader: (pageIndex: number) => Promise<any>;

  constructor() {
    this.moviesLoader = allMoviesProvider();
  }

  static getInstance() {
    if (!this.instance) {
      this.instance = new SmartSearchProvider();
    }
    return this.instance;
  }

  // Generate unique playlist key
  private getPlaylistKey(): string {
    const username = playListUsername();
    const baseUrl = playListUrl();
    const password = playListPassword();
    return `${baseUrl}_${username}_${password}`;
  }

  // Helper function to build API URL with DNS and VPN support
  private async buildApiUrl(
    baseUrl: string,
    username: string,
    password: string,
    action: string,
    additionalParams: string = "",
  ): Promise<string> {
    let apiUrl = `${baseUrl}/player_api.php?username=${username}&password=${password}&action=${action}`;

    if (additionalParams) {
      apiUrl += `&${additionalParams}`;
    }

    // CUSTOM DNS TRANSFORMATION FIRST
    let finalApiUrl = apiUrl;
    try {
      console.log(`🌐 Starting DNS transformation for Smart Search (${action}): ${apiUrl}`);

      const dnsResult = await dnsResolver.transformUrl(apiUrl);

      console.log(`📡 DNS transformation result for Smart Search (${action}):`, dnsResult);

      if (typeof dnsResult === "object" && dnsResult.url) {
        finalApiUrl = dnsResult.url;
        console.log(`✅ Custom DNS applied to Smart Search (${action}): ${apiUrl} → ${finalApiUrl}`);
        // Note: Host header would be handled by fetchData if needed
      } else if (typeof dnsResult === "string" && dnsResult !== apiUrl) {
        finalApiUrl = dnsResult;
        console.log(`🔄 Smart Search URL modified (${action}): ${finalApiUrl}`);
      } else {
        console.log(`📝 Smart Search URL unchanged (${action}) - localhost or DNS disabled`);
      }
    } catch (dnsError) {
      console.warn(`❌ DNS transformation failed for Smart Search (${action}):`, dnsError);
      finalApiUrl = apiUrl; // Use original URL
    }

    // Apply VPN proxy after DNS transformation
    const vpnStatus = localStorage.getItem("vpn");
    const proxyUrl = localStorage.getItem("proxy_url");

    console.log("@@@@@@@VPN", vpnStatus);

    if (vpnStatus === "on" && proxyUrl) {
      const bufferedUrl = Buffer.from(finalApiUrl, "utf-8").toString("base64");
      finalApiUrl = `${proxyUrl}${bufferedUrl}`;
      console.log(`🔒 VPN enabled for Smart Search (${action}) - routing through proxy: ${proxyUrl}`);
    }

    console.log(`📞 Final Smart Search API URL (${action}): ${finalApiUrl}`);
    return finalApiUrl;
  }

  // Check if playlist has changed and clear cache if needed
  private checkPlaylistChange(): void {
    const newPlaylistKey = this.getPlaylistKey();

    if (this.currentPlaylistKey && this.currentPlaylistKey !== newPlaylistKey) {
      console.log("🔄 Playlist changed, re-creating movies loader...");
      this.moviesLoader = allMoviesProvider();
    }

    this.currentPlaylistKey = newPlaylistKey;
  }

  // Load all movies via the allMovies provider (hits its module-level cache)
  private async getAllMovies(): Promise<any[]> {
    const tiles = await this.moviesLoader(0);
    if (!Array.isArray(tiles)) return [];
    // Extract raw movie objects from tiles
    return tiles.map((tile: any) => tile.data || tile);
  }

  // Initialize search — loads all movies in one call (cached if HomePage already fetched them)
  async initializeSearch(): Promise<{ totalMovies: number; categories: string[] }> {
    try {
      this.checkPlaylistChange();

      const allMovies = await this.getAllMovies();

      return {
        totalMovies: allMovies.length,
        categories: [],
      };
    } catch (error) {
      console.error("❌ Error initializing search:", error);
      throw error;
    }
  }

  // Smart search — synchronous filter over the cached movie list
  async smartSearch(
    query: string,
  ): Promise<{
    results: any[];
    searchedCategories: number;
    totalCategories: number;
  }> {
    // Increment search key — any in-flight search with an older key will discard its results
    const myKey = ++this._searchKey;

    // Check if playlist changed
    this.checkPlaylistChange();

    const allMovies = await this.getAllMovies();

    // A newer search started while we were awaiting — discard
    if (this._searchKey !== myKey) {
      return { results: [], searchedCategories: 0, totalCategories: 0 };
    }

    // Empty query — return all movies (capped)
    if (!query.trim()) {
      return {
        results: allMovies.slice(0, 100),
        searchedCategories: 0,
        totalCategories: 0,
      };
    }

    const queryLower = query.toLowerCase().trim();

    const results = allMovies.filter(movie =>
      movie.name?.toLowerCase().includes(queryLower)
    );

    // Deduplicate and cap at 100
    const uniqueResults = results
      .filter(
        (movie, index, self) => index === self.findIndex(m => m.stream_id === movie.stream_id)
      )
      .slice(0, 100);

    return {
      results: uniqueResults,
      searchedCategories: 0,
      totalCategories: 0,
    };
  }

  // Get info about loaded state
  getLoadedInfo() {
    return {
      loadedCategories: 0,
      totalCategories: 0,
      totalMovies: 0,
      categoryNames: [],
      dnsEnabled: dnsResolver.isCustomDnsEnabled(),
      dnsInfo: dnsResolver.getDnsInfo(),
    };
  }

  // Clear cache — re-create the movies loader so it fetches fresh on next call
  clearCache(_specificPlaylist?: string) {
    this.moviesLoader = allMoviesProvider();
    this.currentPlaylistKey = null;
    console.log("🗑️ Search cache cleared (movies loader re-created)");
  }
}

export default SmartSearchProvider;
